/* eslint-disable react/display-name */
import dynamic from 'next/dynamic';
import React, { type CSSProperties, type JSX, memo, type Ref, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { URLParameters } from '@/app/common/urlParameters.ts';
import { useCommonClient } from '@/app/common/commonClientProvider.tsx';
import { useTranslation } from '@/app/components/translations/Translation';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { useSearchPageData } from '@/app/components/search/searchPageDataProvider.tsx';
import { useSearchDealsPageData } from '@/app/components/search/searchDealsPageProvider.tsx';
import { useSearchCommonPageData } from '@/app/components/search/searchCommonPageProvider.tsx';
import useIsClient from '@/app/components/search/hooks/useIsClient.ts';
import { useHasTripAdvisorHotels } from '@/app/common/reviews/useHasTripAdvisorHotels.ts';
import { SortValues } from '@/app/common/memsearch/types.ts';
import { Icon } from '@/app/common/icons/Icon';
import type { StarfishIconRef } from '@/app/common/icons/starfish';
import { RouteType } from '../../../../Curiosity';
import { LoadingBar, PillContainer } from './styled';
import { getLabel, setPosition } from './utils';
import getSearchParams, { getSearchParamsClient } from '../../../../common/getSearchParams';
import { applyResultPageFilterEvent, getFiltersFromQueryString, removeSearchParams } from '../../utils';
import FilterContainer from '../../verticalFilters/FilterContainer';
import type { DynamicFiltersResponse } from '../../../../utils/api/dynamicFiltersResponseTypes';
import Label from './Label';
import PillOverlay from './PillOverlay';

// Black: the pill's dark and selected states recolour them with CSS filters tuned for a black input.
const PILL_ICONS: Record<string, StarfishIconRef> = {
  sort: { category: 'UI Essentials', name: 'icon-sort', color: '#000000' },
  all_filters: { category: 'UI Essentials', name: 'icon-filter', color: '#000000' },
  arrow_small: { category: 'Arrows', name: 'icon-small-chevron-down', color: '#000000' },
  view_map: { category: 'Location', name: 'icon-map', color: '#000000' },
  view_list: { category: 'UI Essentials', name: 'icon-list-view', color: '#000000' },
};

const FiltersCount = dynamic(() => import('./FiltersCount'), { ssr: false });
const PillOverlayModal = dynamic(() => import('../../../Modal/Modals/PillOverlayModal'), { ssr: false });

type PillProps = {
  text: string | React.ReactNode;
  overlayTitle?: string;
  control?: string;
  filterName: string;
  iconLeft?: string;
  iconRight?: string;
  size: number;
  width?: number;
  routeType: RouteType;
  isWaiting: boolean;
  hasOverlay?: boolean;
  selected?: boolean;
  hasCta?: boolean;
  style?: CSSProperties;
  dynamicFiltersResponse?: DynamicFiltersResponse;
  showMapButtonMobile?: boolean;
  handleShowFilters?: (val: boolean) => void;
  isToast?: boolean;
  isDark?: boolean;
  hideDivider?: boolean;
  CustomModal?: React.ComponentType<{ isOpen: boolean; onClose: () => void }>;
};

const Pill = memo(
  ({
    text,
    overlayTitle,
    control,
    filterName,
    iconRight,
    iconLeft,
    size,
    width,
    routeType,
    isWaiting,
    hasOverlay,
    selected,
    hasCta,
    style,
    dynamicFiltersResponse,
    showMapButtonMobile,
    handleShowFilters,
    isToast,
    isDark,
    hideDivider,
    CustomModal,
  }: PillProps): JSX.Element => {
    const isClient = useIsClient();
    const { searchParams: searchParamsServer, localization } = usePageWrapperData();
    const { productsData } = useSearchPageData();
    const hasTripAdvisorHotels = useHasTripAdvisorHotels();
    const { isDesktop, starTrack } = useCommonClient();
    const { t } = useTranslation();
    const { handleFiltersChanged } = useSearchCommonPageData();
    const { handleViewModeSwitch } = useSearchDealsPageData();
    const [open, setOpen] = useState(false);
    const [showCustomModal, setShowCustomModal] = useState(false);
    const extendedFilters = isDesktop;
    const searchParams = getSearchParamsClient(searchParamsServer);
    const overlayRef = useRef<HTMLDivElement>(undefined);
    const containerRef = useRef<HTMLButtonElement>(undefined);
    const wasOverlayOpenRef = useRef(false);
    const wasCustomModalOpenRef = useRef(false);
    const hasCustomModal = !!CustomModal;
    const showSelectedLabel =
      (selected || control === FilterContainer.SortContainer) && control !== FilterContainer.PinkPeopleContainer;
    const label = hasCustomModal
      ? text
      : (showSelectedLabel &&
          getLabel({
            control,
            searchParams,
            localization,
            productsData,
            dynamicFiltersResponse,
            t,
            isTripAdvisor: hasTripAdvisorHotels,
          })) ||
        text;
    const pillIcon = PILL_ICONS[iconRight || iconLeft || ''];
    const isAllFilters = !!handleShowFilters;
    const isSortSelected =
      searchParams[URLParameters.SORT] && searchParams[URLParameters.SORT] !== SortValues.Recommended;
    const showDot = (control === FilterContainer.SortContainer && !extendedFilters && isSortSelected) || isAllFilters;

    const handleRemoveFilter = (e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
      e.stopPropagation();
      containerRef?.current?.blur();
      const searchParamsFresh = getSearchParams();
      const appliedFilters = removeSearchParams(searchParamsFresh);

      if (filterName === URLParameters.ACCOMODATION_FACILITIES) {
        delete appliedFilters.general_accomodation_facilities;
        delete appliedFilters.family_accomodation_facilities;
        delete appliedFilters.activity_accomodation_facilities;
        delete appliedFilters.wellness_accomodation_facilities;
        delete appliedFilters.food_accomodation_facilities;
      }

      delete appliedFilters[filterName];

      const oldFilters = getFiltersFromQueryString(searchParamsFresh);
      applyResultPageFilterEvent({
        oldFilters,
        appliedFilters,
        searchParams: searchParamsFresh,
        starTrack,
        localization,
      });

      setOpen(false);
      handleFiltersChanged({ searchParams, appliedFilters });
    };

    const icon =
      selected && filterName !== 'sort' ? (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" onClick={(e) => handleRemoveFilter(e)}>
          <circle cx={10} cy={10} r={8} />
          <line x1="7" y1="13" x2="13" y2="7" />
          <line x1="7" y1="7" x2="13" y2="13" />
        </svg>
      ) : (
        // The picture wrapper stays: the pill's styles size, space and rotate the icon through it.
        <picture>{pillIcon && <Icon {...pillIcon} size={20} />}</picture>
      );

    const handleClickForSpecificFilters = () => {
      if (filterName === 'pinkpeople') {
        const appliedFilters = removeSearchParams(searchParams);
        if (searchParams[URLParameters.PINK_PEOPLE] === '1') {
          delete appliedFilters.pinkpeople;
        } else {
          appliedFilters.pinkpeople = '1';
        }
        handleFiltersChanged({ searchParams, appliedFilters });
      }

      if (filterName === 'map_toggle') {
        if (!extendedFilters) {
          if (routeType === RouteType.SEARCHRESULTS && filterName === 'map_toggle') {
            window.location.href = `${window.location.href}&${URLParameters.VIEW_MODE_DESKTOP}=map`;
          } else {
            handleViewModeSwitch();
          }
        }
      }
    };

    const handleClickLogic = () => {
      if (hasCustomModal) {
        setShowCustomModal(true);
        return;
      }

      if (hasOverlay && !isWaiting) {
        setOpen(!open);
      }

      if (!!handleShowFilters && !isWaiting) {
        handleShowFilters(true);
      }

      handleClickForSpecificFilters();
    };

    const handleOnClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.preventDefault();
      e.stopPropagation();
      e.currentTarget.blur();

      handleClickLogic();
    };

    const handleOnKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleClickLogic();
      }
    };

    useEffect(() => {
      if (isClient && open && hasOverlay && extendedFilters) {
        setPosition(containerRef, overlayRef, width);
      }
    }, [open, isClient]);

    // Return focus to triggering element when overlay closes
    useEffect(() => {
      if (open) {
        wasOverlayOpenRef.current = true;
      } else if (wasOverlayOpenRef.current && containerRef?.current) {
        containerRef?.current?.focus();
      }
    }, [open]);

    // Return focus to triggering element when custom modal closes
    useEffect(() => {
      if (showCustomModal) {
        wasCustomModalOpenRef.current = true;
      } else if (wasCustomModalOpenRef.current && containerRef?.current) {
        containerRef?.current?.focus();
      }
    }, [showCustomModal]);

    return (
      <>
        <PillContainer
          onClick={(e) => handleOnClick(e)}
          onKeyDown={(e) => handleOnKeyDown(e)}
          open={open}
          ref={containerRef as Ref<HTMLButtonElement>}
          size={size}
          hasIconLeft={iconLeft !== undefined}
          hasIconRight={!!iconRight}
          selected={selected}
          style={style}
          isAllFilters={isAllFilters}
          tabIndex={0}
          id={`Pill-${control}`}
          className={`Pill-${control} ${filterName ? `Pill-${filterName}` : ''}`}
          localization={localization}
          showMapButtonMobile={!!showMapButtonMobile}
          isWaiting={isWaiting}
          isToast={isToast}
          isDark={isDark}
          isDesktop={isDesktop}
          hideDivider={hideDivider}
          aria-expanded={open}
        >
          {isWaiting && isDesktop && !hasCustomModal ? (
            <LoadingBar label={label as string} isSelected={!!selected} />
          ) : (
            <>
              {iconLeft && icon}{' '}
              {hasCustomModal ? label : <Label label={label as string} control={control} selected={!!selected} />}
              {iconRight && icon}
              {isClient && showDot && (
                <FiltersCount withNumber={extendedFilters} isAllFilters={isAllFilters} showOnRight={false} />
              )}
            </>
          )}
        </PillContainer>
        {isClient &&
          extendedFilters &&
          open &&
          hasOverlay &&
          ReactDOM.createPortal(
            <PillOverlay
              overlayRef={overlayRef}
              routeType={routeType}
              isWaiting={isWaiting}
              control={control}
              filterName={filterName}
              hasCta={hasCta}
              setOpen={setOpen}
              dynamicFiltersResponse={dynamicFiltersResponse}
            />,
            document.body
          )}
        {isClient && !extendedFilters && hasOverlay && (
          <PillOverlayModal
            routeType={routeType}
            isWaiting={isWaiting}
            control={control}
            filterName={filterName}
            hasCta={hasCta}
            overlayTitle={overlayTitle}
            isOpen={open}
            setOpen={setOpen}
            dynamicFiltersResponse={dynamicFiltersResponse}
          />
        )}
        {isClient && hasCustomModal && CustomModal && (
          <CustomModal isOpen={showCustomModal} onClose={() => setShowCustomModal(false)} />
        )}
      </>
    );
  },
  (prevProps: PillProps, nextProps: PillProps) => {
    if (JSON.stringify(prevProps.dynamicFiltersResponse) !== JSON.stringify(nextProps.dynamicFiltersResponse)) {
      return false;
    }
    if (prevProps.hideDivider !== nextProps.hideDivider) return false;
    if (prevProps.selected !== nextProps.selected) return false;
    if (prevProps.isWaiting !== nextProps.isWaiting) return false;
    if (prevProps.style !== nextProps.style) return false;
    if (prevProps.CustomModal !== nextProps.CustomModal) return false;
    if (prevProps.width !== nextProps.width) return false;
    if (prevProps.isToast !== nextProps.isToast) return false;
    if (prevProps.isDark !== nextProps.isDark) return false;
    if (prevProps.text !== nextProps.text) return false;
    return true;
  }
);

export default Pill;
